import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Button, Paper, TextField, debounce } from '@mui/material';
import { Hash } from 'lucide-react';

import { FilterChip } from '@modules/_shared/components/filters/quick-filters/FilterChip';
import type { QuickFilterProps } from '@modules/_shared/components/filters/quick-filters/QuickFilters';
import { useFiltersSearchParams } from '@modules/_shared/components/filters/useFiltersSearchParams';
import { useLocale } from '@shared/hooks/locale';

const textFieldSx = {
  minWidth: 150,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'grey.300' },
};

type NumberFilterProps = Omit<
  QuickFilterProps,
  'filter' | 'displayedColumn' | 'type' | 'getTypeFromPath'
>;

export const getNumberDefaultValue = (valueFromPath: any) => {
  const minVal = valueFromPath?._gte;
  const maxVal = valueFromPath?._lte;

  return {
    minVal,
    maxVal,
  };
};

export const NumberFilter = ({
  label,
  addWhereClause,
  deleteWhereClause,
  getValueFromPath,
  where,
  path,
  onChange,
  queryParamsScope,
  disabled,
}: NumberFilterProps) => {
  const { t, locale } = useLocale();
  const { minVal: defaultMinVal, maxVal: defaultMaxVal } =
    getNumberDefaultValue(getValueFromPath(path, where));

  const isDirty = useRef(false);

  const [min, setMin] = useState<number | null>(defaultMinVal);
  const [max, setMax] = useState<number | null>(defaultMaxVal);
  const [, setFiltersParams] = useFiltersSearchParams(queryParamsScope);

  useEffect(() => {
    if (defaultMinVal == null) {
      setMin(null);
    }

    if (defaultMaxVal == null) {
      setMax(null);
    }
  }, [defaultMinVal, defaultMaxVal]);

  const computedLabel = useMemo(() => {
    if (max != null && min == null) {
      return `${label}: ≤ ${max.toLocaleString(locale)}`;
    }

    if (max == null && min != null) {
      return `${label}: ≥ ${min.toLocaleString(locale)}`;
    }

    if (max != null && min != null) {
      return `${label}: ${min.toLocaleString(locale)} - ${max.toLocaleString(
        locale,
      )}`;
    }

    return label;
  }, [label, min, max, locale]);

  const apply = useCallback(
    (minVal: number | null, maxVal: number | null) => {
      let newWhere = deleteWhereClause(where, path, true);
      if (minVal != null) {
        newWhere = addWhereClause(newWhere, [...path, '_gte'], minVal);
      }
      if (maxVal != null) {
        newWhere = addWhereClause(newWhere, [...path, '_lte'], maxVal);
      }

      setFiltersParams(
        onChange?.({ min: minVal, max: maxVal }, newWhere) ?? newWhere,
      );
    },
    [
      where,
      path,
      addWhereClause,
      deleteWhereClause,
      setFiltersParams,
      onChange,
    ],
  );

  const debouncedOnChange = useMemo(() => {
    return debounce((minVal: number | null, maxVal: number | null) => {
      isDirty.current = false;
      apply(minVal, maxVal);
    }, 500);
  }, [apply]);

  const clear = () => {
    let newWhere = where;
    setMin(null);
    setMax(null);
    newWhere = deleteWhereClause(newWhere, path, true);
    setFiltersParams(
      onChange?.({ min: null, max: null }, newWhere) ?? newWhere,
    );
  };

  const applyIfDirty = useCallback(() => {
    // Prevents triggering apply if already done through onChange debounce
    if (isDirty.current) {
      // Clears queued on change
      debouncedOnChange.clear();
      isDirty.current = false;
      apply(min, max);
    }
  }, [debouncedOnChange, apply, min, max]);

  return (
    <>
      <FilterChip
        label={computedLabel}
        icon={<Hash size={16} />}
        onDelete={min || max ? clear : undefined}
        disabled={disabled?.(where) ?? false}
        renderFilter={({ handleClose }) => (
          <Paper
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 2,
              boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            }}
          >
            <form
              onSubmit={e => {
                e.preventDefault();
                applyIfDirty();
                handleClose();
              }}
            >
              <Box
                sx={{
                  p: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: 1,
                }}
              >
                <TextField
                  autoFocus
                  variant="outlined"
                  placeholder={t('Min')}
                  type="number"
                  size="small"
                  autoComplete="one-time-code"
                  onBlur={applyIfDirty}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const newMin = isNaN(e.target.valueAsNumber)
                      ? null
                      : e.target.valueAsNumber;

                    isDirty.current = true;
                    setMin(newMin);
                    debouncedOnChange(newMin, max);
                  }}
                  value={min}
                  sx={textFieldSx}
                />
                <TextField
                  variant="outlined"
                  placeholder={t('Max')}
                  type="number"
                  size="small"
                  autoComplete="one-time-code"
                  onBlur={applyIfDirty}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const newMax = isNaN(e.target.valueAsNumber)
                      ? null
                      : e.target.valueAsNumber;

                    isDirty.current = true;
                    setMax(newMax);
                    debouncedOnChange(min, newMax);
                  }}
                  value={max}
                  sx={textFieldSx}
                />
                <Button
                  onClick={() => {
                    applyIfDirty();
                    handleClose();
                  }}
                  size="small"
                  type="submit"
                >
                  {t('Apply')}
                </Button>
              </Box>
            </form>
          </Paper>
        )}
      />
    </>
  );
};
